import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axios from 'axios';
import { Buffer } from 'buffer';
import {
  ExplorerName,
  ExplorerSummary,
  ItemDetailsWrapper,
  SeriesItemWrapper,
  Thumbnail,
  ThumbnailLoading
} from './styled/StyledExplorer';
import isDocument from '../../../model/isDocument';
import { getHeadersConfig } from '../../../util/getHeadersConfig';
import isWaveform from '../../../model/isWaveform';
import { main } from '../../../themes/main';
import { addSubdirectoryToPath } from '../../../util/getConfig';
import { getSelectedPacsConfig } from '../../../util/pacsSelectorConfig';

// TODO: pending task: substitute hardcoded path by imported pngs (do it in all project, so then we can send generated bundles to client);
const thumbnailECGSrc = addSubdirectoryToPath('/meta/images/thumbnailECG.png');
const thumbnailDOCSrc = addSubdirectoryToPath('/meta/images/thumbnailDOC.png');

const generateThumb = ({ serieModality, firstSOPClassUID }) => {
  if (isWaveform(firstSOPClassUID)) return thumbnailECGSrc;
  if (isDocument(firstSOPClassUID)) return thumbnailDOCSrc;

  if (serieModality === undefined) return '';

  const canvas = document.createElement('canvas');
  const name = serieModality.toUpperCase();

  const ctx = canvas.getContext('2d');
  canvas.width = 80;
  canvas.height = 80;
  ctx.font = '100 20px Arial';
  ctx.fillStyle = main.colors.greyWhite; // '#bbb5c2';
  ctx.textAlign = 'center';
  ctx.fillText(name, canvas.width * 0.5, canvas.height * 0.5);

  return canvas.toDataURL();
};

const searchModalityString = (obj) => {
  const keys = obj instanceof Object && Object.keys(obj);
  let objName;

  if (keys) {
    for (let i = 0; i < keys.length; i++) {
      if (!(obj[keys[i]] instanceof Object)) {
        objName = obj[keys[i]];
        break;
      }
    }
  }
  return !keys ? obj : objName;
};

const getSeriesMetadata = (study, series) => {
  const firstImage = 0; // numImages ? Math.ceil(numImages / 2) - 1 : 0;

  return {
    study,
    series: {
      ...series,
      SeriesInstanceUID: series.instances[firstImage].metadata.SeriesInstanceUID,
    },
    serieModality: series.instances[firstImage].metadata.Modality,
    firstSOPClassUID: series.instances[firstImage].metadata.SOPClassUID,
    firstSOPInstanceUID: series.instances[firstImage].metadata.SOPInstanceUID,
  };
};

const getThumbnailEndpoint = ({ appConfig, seriesMetadata, wadoBaseUrl }) => {
  const { study, series, firstSOPInstanceUID } = seriesMetadata;

  const selectedPacs = getSelectedPacsConfig(appConfig);
  let thumbnail = selectedPacs?.thumbnailUrl || appConfig.WADO_RS_THUMBNAIL_ENDPOINT || 'thumbnail';
  let frames = 'frames/1/';

  const match = thumbnail.match(/^(instance|proxy):(.*)$/);

  if (match) {
    thumbnail = match[2];
    frames = '';
  }
  return `${wadoBaseUrl}/studies/${study.StudyInstanceUID}/series/${series.SeriesInstanceUID}/instances/${firstSOPInstanceUID}/${frames}${thumbnail}`;
};

const retryableThumbnailStatusCodes = [429, 500];

const waitForRetry = (delay, signal) => new Promise((resolve) => {
  let timeout;
  const finish = () => {
    clearTimeout(timeout);
    signal.removeEventListener('abort', finish);
    resolve();
  };
  timeout = setTimeout(finish, delay);
  signal.addEventListener('abort', finish, { once: true });
});

function retrieveThumbnail(url, config, signal, attempt = 0) {
  if (signal.aborted) return Promise.resolve(undefined);
  const retries = 2;

  return axios.get(url, { ...config, signal }).catch(async (error) => {
    if (signal.aborted) return undefined;
    const status = error.response && error.response.status;
    const shouldRetry = retryableThumbnailStatusCodes.includes(status)
      && attempt < retries;

    if (!shouldRetry) {
      return undefined;
    }

    await waitForRetry((attempt + 1) * 250, signal);
    return signal.aborted ? undefined : retrieveThumbnail(url, config, signal, attempt + 1);
  });
}

async function getThumbnailRawData({ appConfig, servers, seriesMetadata, signal }) {
  let data;

  const mapThumbnailEndpoint = (server) => getThumbnailEndpoint({
    appConfig,
    seriesMetadata,
    wadoBaseUrl: server.wadoRoot,
  });
  const config = getHeadersConfig(appConfig.authorizationHeader, appConfig.WADO_RS_THUMBNAIL_ACCEPT_HEADER);

  // eslint-disable-next-line no-restricted-syntax
  for (const url of servers.map(mapThumbnailEndpoint)) {
    if (signal.aborted) break;
    // eslint-disable-next-line no-await-in-loop
    data = await retrieveThumbnail(url, config, signal);

    if (data || signal.aborted) break;
  }
  return data;
}

export const SeriesItem = ({ study, series, onClick, onDrag, currentSerieUID, servers, appConfig, selected }) => {
  const [thumbnailSrc, setThumbnailSrc] = useState(undefined);
  const seriesMetadata = getSeriesMetadata(study, series);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      const rawData = await getThumbnailRawData({
        appConfig,
        servers,
        seriesMetadata,
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      if (rawData) {
        const base64ImageString = Buffer.from(rawData.data, 'binary').toString('base64');
        setThumbnailSrc(`data:image/png;base64,${base64ImageString}`);
        return;
      }

      const thumbnail = generateThumb(seriesMetadata);
      setThumbnailSrc(thumbnail);
    })();

    return () => {
      controller.abort();
    };
  }, [appConfig, servers, series, study]);

  const studyDate = series.seriesDate || (study && study.StudyDate);
  const formattedDate = studyDate && moment(studyDate).format('DD[/]MM[/]YY');
  const { t } = useTranslation();
  const numImages = series.instances.length;

  const displayPicture = () => (thumbnailSrc !== undefined ? <Thumbnail src={thumbnailSrc} /> : <ThumbnailLoading>{t('loading')}</ThumbnailLoading>);

  return (
    <SeriesItemWrapper className={currentSerieUID === series.SeriesInstanceUID ? 'active' : ''} onClick={onClick} draggable="true" onDragStart={onDrag} selected={selected}>
      {displayPicture()}
      <ItemDetailsWrapper>
        <ExplorerName>{series.SeriesDescription}</ExplorerName>
        <ExplorerName>{formattedDate}</ExplorerName>
        <span>{searchModalityString(series.Modality)}</span>
        <ExplorerSummary>{numImages === 0 ? <span>{t('no.images')}</span> : <span>{`${numImages} ${t(`image${numImages !== 1 ? 's' : ''}`)}`}</span>}</ExplorerSummary>
      </ItemDetailsWrapper>
    </SeriesItemWrapper>
  );
};

export default React.memo(SeriesItem);
